import { useState } from 'react';
import { groupBy } from 'lodash-es';

import { Link, Stack } from '@hotjar/hotsauce';
import { useProductCatalog } from '@hotjar/webapp/domains/common/product/hooks/useProductCatalog';
import { getProductsByProductType } from '@hotjar/webapp/domains/common/product/utils';
import { sortFeatureGroups } from '@hotjar/webapp/domains/revenue/plan/utils/sortFeatureGroups';
import type { Product, ProductType } from '@hotjar/webapp/domains/common/product/types';
import type { FeatureGroup } from '@hotjar/webapp/domains/revenue/plan/types';
import type { Site } from '@hotjar/site';
import { useTrack } from '@hotjar/webapp/hooks/useTrack';
import { PlanFeaturesToggle } from '@hotjar/webapp/features/Billing/Upsell/components/PlanFeaturesToggle/PlanFeaturesToggle';
import { PlanColumnsStyles } from '@hotjar/webapp/features/Billing/PlanComparisonColumns/styles';
import { Trans } from '@hotjar/translations';
import { HJ_PRICING_PAGE } from '@hotjar/webapp/features/Billing/PlanComparisonModal/constants';
import type { BillingPeriod } from '@hotjar/subscription-management';

import type {
  SelectedProducts,
  ChangeSelectedProducts,
} from '../../../MultiProductWrapper/hooks/useMultiProductsState';
import { PlanSelectionItem } from './components/PlanSelectionItem';

interface PlanSelectionProps {
  site: Site;
  productType: ProductType;
  selectedProducts: SelectedProducts;
  changeSelectedProducts: ChangeSelectedProducts;
  recommendedProduct: Product | null;
  currentProduct: Product | null;
  billingPeriod: BillingPeriod;
}

export const PlanSelection = ({
  site,
  productType,
  selectedProducts,
  changeSelectedProducts,
  recommendedProduct,
  currentProduct,
  billingPeriod,
}: PlanSelectionProps): JSX.Element | null => {
  const [showFeaturesList, setShowFeaturesList] = useState<boolean>(false);
  const { click } = useTrack();
  const { data: productCatalog } = useProductCatalog();

  const products = getProductsByProductType(productType, productCatalog);
  const groupedProducts = groupBy(products, 'tier');

  const sortedFeatureGroups = sortFeatureGroups(
    Object.keys(groupedProducts || {}) as FeatureGroup[],
  );

  const changeSelectedProduct = (product: Product) => {
    changeSelectedProducts(productType, product);
  };

  const isLowerTier = (tier: FeatureGroup) => {
    return (
      sortedFeatureGroups.indexOf(tier) <
      sortedFeatureGroups.indexOf(currentProduct?.tier || sortedFeatureGroups[0])
    );
  };

  const toggleFeaturesList = () => {
    click(`Plan selection feature list toggle`, 'Multi-product upsell modal', {});
    setShowFeaturesList(!showFeaturesList);
  };

  const selectedProduct = selectedProducts[productType];

  if (!selectedProduct || !currentProduct) {
    return null;
  }

  return (
    <Stack direction="column" spacing="x2">
      <Stack direction="row" spacing="x1pt5" data-testid="plan-selection-modal-plan-selection">
        {groupedProducts &&
          sortedFeatureGroups.map((tier) => {
            return (
              <PlanSelectionItem
                site={site}
                key={tier}
                tier={tier}
                tierProducts={groupedProducts[tier]}
                recommendedProduct={recommendedProduct}
                currentProduct={currentProduct}
                selectedProduct={selectedProduct}
                changeSelectedProduct={changeSelectedProduct}
                isLowerTier={isLowerTier(tier)}
                productType={productType}
                showFeaturesList={showFeaturesList}
                billingPeriod={billingPeriod}
              />
            );
          })}
      </Stack>

      {showFeaturesList && (
        <PlanColumnsStyles.FindOutMore>
          <Trans i18nKey="revenue:plan-comparison-columns.find-out-more">
            <Link href={HJ_PRICING_PAGE} kind="inline" isExternal>
              Find out more about the different plans
            </Link>
          </Trans>
        </PlanColumnsStyles.FindOutMore>
      )}

      <PlanFeaturesToggle isOpen={showFeaturesList} onClick={toggleFeaturesList} />
    </Stack>
  );
};
